/**
 * Offline-Outbox für Zeiteinträge – bewusst ohne Browser-Abhängigkeiten,
 * damit sie mit vitest getestet werden kann. Der Speicher (IndexedDB im
 * Browser, Map im Test) wird injiziert.
 *
 * Ablauf: Jede Änderung landet zuerst hier (mit client-generierter UUID und
 * updatedAt). flush() schickt sie gesammelt an den Server; bestätigte oder
 * vom Server überholte Einträge verschwinden aus der Outbox, abgelehnte
 * bleiben mit Fehlermeldung stehen, bis sie bearbeitet oder verworfen werden.
 */
import type { EntryChange, EntryDTO, SyncResult } from '../shared/types.js';

export interface OutboxItem {
  id: string;
  entry: EntryChange;
  queuedAt: number;
  attempts: number;
  /** Fehlermeldung des Servers (abgelehnt) – solche Einträge werden nicht erneut gesendet. */
  error: string | null;
}

export interface OutboxStorage {
  load(): Promise<OutboxItem[]>;
  save(items: OutboxItem[]): Promise<void>;
}

/** Sender: schickt Änderungen an den Server. Wirft NetworkError, wenn offline. */
export type Sender = (changes: EntryChange[]) => Promise<SyncResult[]>;

export class NetworkError extends Error {
  constructor(message = 'Keine Verbindung') {
    super(message);
    this.name = 'NetworkError';
  }
}

export interface FlushReport {
  sent: number;
  ok: EntryDTO[];
  conflicts: EntryDTO[];
  rejected: { id: string; error: string; entry?: EntryDTO }[];
  warnings: { id: string; warnings: string[] }[];
  offline: boolean;
  error?: string;
}

export function memoryStorage(initial: OutboxItem[] = []): OutboxStorage & { data: OutboxItem[] } {
  const s = {
    data: structuredClone(initial),
    async load() {
      return structuredClone(s.data);
    },
    async save(items: OutboxItem[]) {
      s.data = structuredClone(items);
    },
  };
  return s;
}

export class Outbox {
  private items: OutboxItem[] | null = null;
  private running: Promise<FlushReport> | null = null;
  private listeners = new Set<() => void>();

  constructor(
    private storage: OutboxStorage,
    private opts: { batchSize?: number; now?: () => number } = {},
  ) {}

  private async all(): Promise<OutboxItem[]> {
    if (!this.items) this.items = await this.storage.load();
    return this.items;
  }

  private async persist(): Promise<void> {
    await this.storage.save(this.items ?? []);
    for (const l of this.listeners) l();
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  async list(): Promise<OutboxItem[]> {
    return [...(await this.all())];
  }

  /** Anzahl wartender (nicht abgelehnter) Änderungen. */
  async pendingCount(): Promise<number> {
    return (await this.all()).filter((i) => !i.error).length;
  }

  async failed(): Promise<OutboxItem[]> {
    return (await this.all()).filter((i) => i.error);
  }

  /** Änderung einreihen. Mehrere Änderungen am selben Eintrag werden zusammengefasst (letzte gewinnt). */
  async enqueue(entry: EntryChange): Promise<void> {
    const items = await this.all();
    const idx = items.findIndex((i) => i.id === entry.id);
    const item: OutboxItem = { id: entry.id, entry: structuredClone(entry), queuedAt: this.now(), attempts: 0, error: null };
    if (idx >= 0) {
      // Der Server kennt die wartende Vorversion noch nicht: deren Basis gilt weiter.
      // Hatte sie keine Basis (Stempeluhr), bleibt es bei last-write-wins.
      const prev = items[idx].entry;
      if (prev.baseUpdatedAt === undefined) delete item.entry.baseUpdatedAt;
      else item.entry.baseUpdatedAt = prev.baseUpdatedAt;
      items[idx] = item;
    }
    else items.push(item);
    await this.persist();
  }

  /** Alles verwerfen (Benutzerwechsel auf dem Gerät). */
  async reset(): Promise<void> {
    this.items = [];
    await this.persist();
  }

  async discard(id: string): Promise<void> {
    const items = await this.all();
    this.items = items.filter((i) => i.id !== id);
    await this.persist();
  }

  /** Sendet wartende Änderungen. Parallel laufende Aufrufe teilen sich einen Durchlauf. */
  flush(send: Sender): Promise<FlushReport> {
    if (!this.running) {
      this.running = this.doFlush(send).finally(() => {
        this.running = null;
      });
    }
    return this.running;
  }

  private now(): number {
    return this.opts.now ? this.opts.now() : Date.now();
  }

  private async doFlush(send: Sender): Promise<FlushReport> {
    const report: FlushReport = { sent: 0, ok: [], conflicts: [], rejected: [], warnings: [], offline: false };
    const batchSize = this.opts.batchSize ?? 50;
    // Schleife, bis alles gesendet ist oder ein Fehler auftritt.
    for (;;) {
      const batch = (await this.all()).filter((i) => !i.error).slice(0, batchSize);
      if (!batch.length) return report;
      const sentVersions = new Map(batch.map((i) => [i.id, i.entry.updatedAt]));
      let results: SyncResult[];
      try {
        results = await send(batch.map((i) => i.entry));
      } catch (err) {
        for (const i of batch) {
          const cur = (await this.all()).find((x) => x.id === i.id);
          if (cur) cur.attempts++;
        }
        await this.persist();
        report.offline = err instanceof NetworkError;
        report.error = (err as Error).message;
        return report;
      }
      report.sent += batch.length;
      const items = await this.all();
      const handled = new Set<string>();
      for (const r of results) {
        const cur = items.find((i) => i.id === r.id);
        // Während des Sendens erneut bearbeitet? Dann die neue Version behalten.
        const unchanged = cur && cur.entry.updatedAt === sentVersions.get(r.id);
        if (r.status === 'ok') {
          if (r.entry) report.ok.push(r.entry);
          if (r.warnings?.length) report.warnings.push({ id: r.id, warnings: r.warnings });
          if (unchanged) handled.add(r.id);
          // Während des Sendens erneut bearbeitet: Folgeänderung beruht jetzt auf dem gespeicherten Stand.
          else if (cur && r.entry && cur.entry.baseUpdatedAt !== undefined) cur.entry.baseUpdatedAt = r.entry.updatedAt;
        } else if (r.status === 'conflict') {
          if (r.entry) report.conflicts.push(r.entry);
          if (unchanged) handled.add(r.id);
        } else {
          report.rejected.push({ id: r.id, error: r.error ?? 'Abgelehnt', entry: r.entry });
          if (cur && unchanged) cur.error = r.error ?? 'Abgelehnt';
        }
      }
      this.items = items.filter((i) => !handled.has(i.id));
      await this.persist();
      // Kein Fortschritt (z. B. Server hat nichts beantwortet) → abbrechen, um Endlosschleifen zu vermeiden.
      if (!results.length) return report;
    }
  }
}

export type LocalEntry = EntryDTO & { pending: boolean; error: string | null };

/**
 * Lokale Sicht: Serverstand + noch nicht synchronisierte Änderungen aus der
 * Outbox (die Outbox-Version gewinnt, wenn sie neuer oder gleich alt ist).
 * Gelöschte Einträge (Tombstones) werden ausgeblendet.
 */
export function mergeLocal(server: EntryDTO[], outbox: OutboxItem[], userId: number, filter?: (e: EntryDTO) => boolean): LocalEntry[] {
  const map = new Map<string, LocalEntry>();
  for (const e of server) map.set(e.id, { ...e, pending: false, error: null });
  for (const i of outbox) {
    const s = map.get(i.id);
    if (s && s.updatedAt > i.entry.updatedAt && !i.error) continue;
    const base: EntryDTO = { ...i.entry, userId: i.entry.userId ?? userId, late: s?.late ?? false };
    map.set(i.id, { ...base, pending: true, error: i.error });
  }
  return [...map.values()]
    .filter((e) => !e.deleted || e.error)
    .filter((e) => (filter ? filter(e) : true))
    .sort((a, b) => a.date.localeCompare(b.date) || a.start.localeCompare(b.start));
}
